import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { formatMoney, formatDate, formatDateRange } from "@/lib/api";
import { displayFor } from "@/lib/bills-util";
import { MiniRow } from "./shared";

// Rolling 30-day windows: today→+30, +30→+60, +60→+90. Renders in place of
// "Your Next Pay Periods" when the Dashboard toggle is set to Next 30 days.
// Uses the same visual card language as pay-period and month strips so the
// Dashboard reads consistently across all three modes.
//
// Numbers on each card are frontend-projected (no API round-trip):
//   • Incomes: expands recurring rows (weekly/biweekly/monthly) into
//     occurrences that land inside the 30-day window.
//   • Bills: filters `bills` by due_date within the window and totals
//     everything that hasn't yet cleared the bank (unpaid + pending +
//     paid-but-not-cleared).
//   • End balance: cumulative — window 1 starts from real bank balance,
//     window 2 starts from window 1's end balance, etc.
export default function NextRollingWindowsStrip({
  bills = [],
  incomes = [],
  onOpenIncomeEvent,
}) {
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Materialized "received" occurrences of recurring paychecks — when a user
  // marks a specific pay-date received, the backend writes an unscheduled
  // income entry for that date. We must skip the matching future occurrence
  // in the recurring series so it doesn't double-count. Mirrors
  // `forecast.project_incomes_in_window` (see backend/forecast.py).
  const receivedMarks = new Set(
    (incomes || [])
      .filter((i) => i.frequency === "unscheduled" && i.status === "received" && i.expected_date)
      .map((i) => `${i.source || ""}|${i.expected_date}`)
  );

  // Expand a single income row into every occurrence that lands in
  // [start, end]. Mirrors `forecast.project_incomes_in_window`:
  //   • Recurring series (weekly/biweekly/monthly) are NEVER filtered by
  //     the base row's `status` — that status represents the most recent
  //     reconciled paycheck, not the entire future series. Filtering on it
  //     would drop future paydays whenever a user marks the latest one
  //     received (the source of the missing-income bug on Next 30 days).
  //   • One-time (unscheduled) incomes DO honour `status == "expected"`
  //     so already-received one-off money isn't double-counted.
  const projectIncomeOccurrences = (inc, start, end) => {
    const step = { weekly: 7, biweekly: 14, monthly: 30 }[inc.frequency];
    const base = new Date(inc.expected_date + "T00:00:00");
    const endDate = inc.end_date ? new Date(inc.end_date + "T00:00:00") : null;
    if (!step) {
      // Unscheduled / one-time — skip already-received rows.
      if (inc.status !== "expected") return [];
      return (base >= start && base <= end) ? [{
        income_id: inc.id, source: inc.source, amount: Number(inc.amount || 0),
        expected_date: inc.expected_date, frequency: inc.frequency,
      }] : [];
    }
    // Recurring — walk forward, ignoring base status.
    let cur = new Date(base);
    while (cur < start) cur = new Date(cur.getTime() + step * 86400000);
    while (cur > start && new Date(cur.getTime() - step * 86400000) >= start) {
      cur = new Date(cur.getTime() - step * 86400000);
    }
    const out = [];
    while (cur <= end) {
      if (endDate && cur > endDate) break;
      if (cur >= start) {
        const iso = cur.toISOString().slice(0, 10);
        // Skip occurrences the user already marked received.
        if (!receivedMarks.has(`${inc.source || ""}|${iso}`)) {
          out.push({
            income_id: inc.id,
            source: inc.source,
            amount: Number(inc.amount || 0),
            expected_date: iso,
            frequency: inc.frequency,
          });
        }
      }
      cur = new Date(cur.getTime() + step * 86400000);
    }
    return out;
  };

  // Build the 3 windows — non-overlapping 30-day slices starting AFTER the
  // immediate "Next 30 days" view the Dashboard already shows above.
  //   • Card 1 "Next 60 days"  → days 31-60
  //   • Card 2 "Next 90 days"  → days 61-90
  //   • Card 3 "Next 120 days" → days 91-120
  // Both boundaries are inclusive but consecutive windows never share a
  // boundary date, so bills due exactly on the seam don't double-count.
  const windows = [[31, 60], [61, 90], [91, 120]].map(([startOffset, endOffset], idx) => {
    const start = new Date(today);
    start.setDate(start.getDate() + startOffset);
    const end = new Date(today);
    end.setDate(end.getDate() + endOffset);
    // Incomes projected in this window.
    const incomeEvents = incomes.flatMap((i) => projectIncomeOccurrences(i, start, end));
    // Dedupe by (source, date) to guard against overlapping recurring rows.
    const seen = new Set();
    const uniqueIncomes = incomeEvents.filter((e) => {
      const k = `${e.source || ""}|${e.expected_date}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    const incomeTotal = uniqueIncomes.reduce((s, e) => s + Number(e.amount || 0), 0);
    // Bills due in this window that haven't cleared the bank yet.
    const windowBills = bills.filter((b) => {
      const d = new Date(b.due_date + "T00:00:00");
      if (d < start || d > end) return false;
      // Skip already-cleared bills (money's out).
      if (b.status === "cleared") return false;
      // Skip fully-paid AND fully-cleared bills.
      if (b.status === "paid" && !displayFor(b, today).notCleared) return false;
      return true;
    });
    const billsTotal = windowBills.reduce((s, b) => s + Number(b.estimated_due_amount || 0), 0);
    return {
      idx,
      // Label represents how far out the window ends ("Next 60 days" = the
      // 30-day slice ending 60 days from today). The date range subtitle
      // below resolves any ambiguity for users who read it as "day 1-60".
      label: `Next ${endOffset} days`,
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
      incomes: uniqueIncomes,
      incomeTotal,
      billsCount: windowBills.length,
      billsTotal,
      netFlow: incomeTotal - billsTotal,
    };
  });

  // Each window is scored INDEPENDENTLY — no starting balance is chained
  // from one card to the next. Rationale: the further out we look, the more
  // untracked spending accumulates, so a compounded "end balance" would be
  // pure fiction. Each card answers a self-contained question:
  //   "In that 30-day slice, do the paychecks cover the bills?"
  const projected = windows;


  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8A918D]">Ahead</div>
          <h2 className="font-heading text-xl font-medium text-[#2C302E]">Looking further out</h2>
          <p className="text-xs text-[#59605C] mt-1">Three independent 30-day slices past the immediate month above. Each card answers a single question — <span className="font-medium text-[#2C302E]">do that window&rsquo;s paychecks cover its bills?</span> — with no balance carried between cards.</p>
        </div>
        <Link to="/app/pay-periods" className="btn-ghost text-sm" data-testid="dashboard-rolling-link">See planner →</Link>
      </div>
      <div className="grid md:grid-cols-3 gap-4" data-testid="dashboard-rolling-grid">
        {projected.map((w) => (
          <div
            key={w.idx}
            className="p-4 rounded-2xl bg-[#F9F8F6] border-2 border-[#D8CFB9]"
            data-testid={`dashboard-rolling-${w.idx}`}
          >
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Window</div>
            <div className="font-heading text-base text-[#2C302E] leading-snug" data-testid={`dashboard-rolling-label-${w.idx}`}>
              {w.label}
            </div>
            <div className="text-[11px] text-[#8A918D] mt-0.5">{formatDateRange(w.start, w.end)}</div>
            {w.incomes.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-[#3B5B3E] mb-0.5">Scheduled income</div>
                <div className="space-y-0.5">
                  {w.incomes.map((inc, j) => (
                    <button
                      type="button"
                      key={j}
                      className="w-full flex justify-between text-xs hover:bg-[#F1EEE7] rounded px-1 -mx-1 py-0.5 transition-colors"
                      onClick={() => onOpenIncomeEvent && onOpenIncomeEvent({
                        income_id: inc.income_id,
                        source: inc.source,
                        amount: Number(inc.amount || 0),
                        date: inc.expected_date,
                        frequency: inc.frequency,
                      })}
                      title="Edit this occurrence or the whole recurring source"
                      data-testid={`dashboard-rolling-inc-edit-${w.idx}-${j}`}
                    >
                      <span className="text-[#59605C] truncate mr-2 underline decoration-dotted underline-offset-2">
                        {inc.source} · {formatDate(inc.expected_date)}
                      </span>
                      <span className="text-[#3B5B3E]">+{formatMoney(inc.amount)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4 space-y-1 text-sm border-t border-[#EBE6E0] pt-3">
              <MiniRow label="Total scheduled income" value={`+${formatMoney(w.incomeTotal)}`} pos />
              <Link
                to={`/app/bills?from=${w.start}&to=${w.end}`}
                className="flex justify-between text-sm text-[#59605C] hover:text-[#4A6148] hover:underline decoration-dotted underline-offset-2 focus:outline-none"
                title="See every bill due in this window"
                data-testid={`dashboard-rolling-bills-${w.idx}`}
              >
                <span>{`Bills due (${w.billsCount})`}</span>
                <span className="text-[#8C3A30]">-{formatMoney(w.billsTotal)}</span>
              </Link>
              <div className="pt-2 mt-1 border-t border-[#EBE6E0] flex justify-between font-semibold text-[#2C302E]" data-testid={`dashboard-rolling-balance-${w.idx}`}>
                <span>Net for this window</span>
                <span className={w.netFlow < 0 ? "text-[#8C3A30]" : "text-[#3B5B3E]"}>
                  {w.netFlow >= 0 ? "+" : ""}{formatMoney(w.netFlow)}
                </span>
              </div>
              <Link
                to="/app/income?add=1"
                className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#EEF3EC] text-[#3B5B3E] hover:bg-[#D5E1D3] transition-colors"
                title="Add an expected paycheck or one-time deposit"
                data-testid={`dashboard-rolling-add-income-${w.idx}`}
              >
                <Plus size={11} /> Add expected income
              </Link>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
